import React, { useEffect, useMemo, useState } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { resolveApiUrl } from "../../helpers/api";

type Platform = "android" | "ios";

type VersionPolicy = {
  minVersion: string;
  latestVersion?: string;
  updateUrl?: string;
  message?: string;
};

type RequireSupportedVersionProps = {
  children: React.ReactNode;
};

const VERSION_POLICY_ENDPOINT =
  process.env.REACT_APP_VERSION_POLICY_ENDPOINT || "/api/app/version-policy";

const ENV_ANDROID_MIN_VERSION = process.env.REACT_APP_MIN_ANDROID_VERSION || "";
const ENV_ANDROID_UPDATE_URL = process.env.REACT_APP_ANDROID_UPDATE_URL || "";
const ENV_IOS_MIN_VERSION = process.env.REACT_APP_MIN_IOS_VERSION || "";
const ENV_IOS_UPDATE_URL = process.env.REACT_APP_IOS_UPDATE_URL || "";

const isAbsoluteHttpUrl = (value: string) => /^https?:\/\//i.test(value);

const getNumericParts = (value: string) =>
  value
    .trim()
    .split(/[^0-9]+/)
    .filter(Boolean)
    .map((token) => Number.parseInt(token, 10))
    .filter((token) => Number.isFinite(token));

const compareVersions = (left: string, right: string) => {
  const leftParts = getNumericParts(left);
  const rightParts = getNumericParts(right);
  const maxLength = Math.max(leftParts.length, rightParts.length);

  for (let idx = 0; idx < maxLength; idx += 1) {
    const leftValue = leftParts[idx] ?? 0;
    const rightValue = rightParts[idx] ?? 0;
    if (leftValue > rightValue) return 1;
    if (leftValue < rightValue) return -1;
  }

  return 0;
};

const readString = (value: unknown) =>
  typeof value === "string" && value.trim().length > 0 ? value.trim() : "";

const parseVersionPolicy = (payload: any): VersionPolicy | null => {
  const candidates = [payload, payload?.data, payload?.policy].filter(Boolean);

  for (const candidate of candidates) {
    const minVersion = readString(
      candidate?.minVersion ??
        candidate?.min_version ??
        candidate?.minimumVersion ??
        candidate?.minimum_version,
    );

    if (!minVersion) continue;

    const latestVersion = readString(
      candidate?.latestVersion ?? candidate?.latest_version,
    );
    const updateUrl = readString(
      candidate?.updateUrl ??
        candidate?.update_url ??
        candidate?.storeUrl ??
        candidate?.store_url,
    );
    const message = readString(candidate?.message);

    return {
      minVersion,
      latestVersion: latestVersion || undefined,
      updateUrl: updateUrl || undefined,
      message: message || undefined,
    };
  }

  return null;
};

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs = 6000) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error("Tiempo de espera agotado al consultar política de versión."));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

const buildPolicyUrl = (platform: Platform) => {
  const endpoint = VERSION_POLICY_ENDPOINT.trim();
  if (!endpoint) return "";

  const baseUrl = isAbsoluteHttpUrl(endpoint) ? endpoint : resolveApiUrl(endpoint);
  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}platform=${platform}`;
};

const buildEnvFallbackPolicy = (platform: Platform): VersionPolicy | null => {
  const minVersion = platform === "android" ? ENV_ANDROID_MIN_VERSION : ENV_IOS_MIN_VERSION;
  const updateUrl = platform === "android" ? ENV_ANDROID_UPDATE_URL : ENV_IOS_UPDATE_URL;

  if (!minVersion) return null;

  return {
    minVersion,
    updateUrl: updateUrl || undefined,
  };
};

const loadPolicy = async (platform: Platform): Promise<VersionPolicy | null> => {
  const policyUrl = buildPolicyUrl(platform);
  if (!policyUrl) {
    return buildEnvFallbackPolicy(platform);
  }

  try {
    const response = await withTimeout(
      fetch(policyUrl, {
        method: "GET",
        headers: { Accept: "application/json" },
      }),
    );

    if (!response.ok) {
      throw new Error(`Error HTTP ${response.status}`);
    }

    const data = await response.json();
    const parsed = parseVersionPolicy(data);
    return parsed ?? buildEnvFallbackPolicy(platform);
  } catch (error) {
    console.error("[version-check] No se pudo cargar la política remota:", error);
    return buildEnvFallbackPolicy(platform);
  }
};

const RequireSupportedVersion = ({ children }: RequireSupportedVersionProps) => {
  const [isChecking, setIsChecking] = useState(true);
  const [requiresUpdate, setRequiresUpdate] = useState(false);
  const [updateMessage, setUpdateMessage] = useState(
    "Esta versión de la app quedó obsoleta. Actualizá para continuar.",
  );
  const [updateUrl, setUpdateUrl] = useState("");
  const [versionSummary, setVersionSummary] = useState("");

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!Capacitor.isNativePlatform()) {
        if (!cancelled) setIsChecking(false);
        return;
      }

      const platform = Capacitor.getPlatform();
      if (platform !== "android" && platform !== "ios") {
        if (!cancelled) setIsChecking(false);
        return;
      }

      const nativePlatform = platform as Platform;

      try {
        const [{ version: currentVersion }, policy] = await Promise.all([
          CapacitorApp.getInfo(),
          loadPolicy(nativePlatform),
        ]);

        if (!policy?.minVersion) {
          if (!cancelled) setIsChecking(false);
          return;
        }

        const isOutdated = compareVersions(currentVersion, policy.minVersion) < 0;
        if (cancelled) return;

        if (isOutdated) {
          setRequiresUpdate(true);
          setUpdateMessage(
            policy.message ||
              "Esta versión de la app quedó obsoleta. Actualizá para continuar.",
          );
          setUpdateUrl(
            policy.updateUrl ||
              (nativePlatform === "android"
                ? ENV_ANDROID_UPDATE_URL
                : ENV_IOS_UPDATE_URL),
          );
          setVersionSummary(
            `Versión instalada: ${currentVersion}. Versión mínima requerida: ${policy.minVersion}${
              policy.latestVersion ? `. Última versión disponible: ${policy.latestVersion}.` : "."
            }`,
          );
          setIsChecking(false);
          return;
        }

        setIsChecking(false);
      } catch (error) {
        console.error("[version-check] Error al verificar versión:", error);
        if (!cancelled) setIsChecking(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, []);

  const helperText = useMemo(() => {
    if (updateUrl) return "";
    return "Falta URL de actualización. Configurá REACT_APP_ANDROID_UPDATE_URL o devolvé updateUrl desde la API.";
  }, [updateUrl]);

  const goToUpdate = () => {
    if (!updateUrl) return;
    window.location.href = updateUrl;
  };

  if (isChecking) {
    return null;
  }

  if (!requiresUpdate) {
    return <>{children}</>;
  }

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light"
      style={{ zIndex: 2147483647 }}
    >
      <div className="card shadow-sm border-0" style={{ width: "min(560px, 92vw)" }}>
        <div className="card-body p-4 p-md-5 text-center">
          <h4 className="mb-3">Actualización obligatoria</h4>
          <p className="text-muted mb-2">{updateMessage}</p>
          {versionSummary ? <p className="small text-muted mb-4">{versionSummary}</p> : null}
          <button
            type="button"
            className="btn btn-primary px-4"
            onClick={goToUpdate}
            disabled={!updateUrl}
          >
            Actualizar ahora
          </button>
          {helperText ? <p className="small text-danger mt-3 mb-0">{helperText}</p> : null}
        </div>
      </div>
    </div>
  );
};

export default RequireSupportedVersion;
